/**
 * AES-256-GCM Encryption/Decryption utility for API payload security.
 * Uses the Web Crypto API — works in both browser and Node.js 18+ (Remix SSR).
 * Compatible with the server's Node.js crypto implementation.
 *
 * Wire format: base64( IV[16 bytes] + AuthTag[16 bytes] + CipherText )
 */

const ENCRYPTION_KEY_HEX = process.env.ENCRYPTION_KEY as string;

// ──────────── helpers ────────────

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function toBase64(buffer: Uint8Array): string {
  // Node.js
  if (typeof Buffer !== "undefined") {
    return Buffer.from(buffer).toString("base64");
  }
  // Browser
  let binary = "";
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

function fromBase64(base64: string): Uint8Array {
  // Node.js
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(base64, "base64"));
  }
  // Browser
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ──────────── key management ────────────

let _cryptoKey: CryptoKey | null = null;

async function getCryptoKey(): Promise<CryptoKey> {
  if (_cryptoKey) return _cryptoKey;

  if (!ENCRYPTION_KEY_HEX) {
    throw new Error(
      "ENCRYPTION_KEY environment variable is required. " +
        "Must be a 64-character hex string (256-bit key)."
    );
  }

  const keyBytes = hexToBytes(ENCRYPTION_KEY_HEX);

  if (keyBytes.length !== 32) {
    throw new Error(
      "ENCRYPTION_KEY must be a 64-character hex string (256-bit key)."
    );
  }

  _cryptoKey = await globalThis.crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );

  return _cryptoKey;
}

// ──────────── public API ────────────

/**
 * Encrypt data using AES-256-GCM.
 * Returns a base64 string in the format: IV (16) + AuthTag (16) + CipherText
 */
export async function encryptPayload(data: unknown): Promise<string> {
  const key = await getCryptoKey();
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const plaintext = new TextEncoder().encode(JSON.stringify(data));

  // Web Crypto AES-GCM returns: CipherText + AuthTag (tag is last 16 bytes)
  const encrypted = await globalThis.crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    key,
    plaintext
  );

  const encryptedArray = new Uint8Array(encrypted);
  const tagStart = encryptedArray.length - 16;
  const cipherText = encryptedArray.slice(0, tagStart);
  const authTag = encryptedArray.slice(tagStart);

  // Server format: IV (16) + AuthTag (16) + CipherText
  const combined = new Uint8Array(16 + 16 + cipherText.length);
  combined.set(iv, 0);
  combined.set(authTag, 16);
  combined.set(cipherText, 32);

  return toBase64(combined);
}

/**
 * Decrypt an AES-256-GCM encrypted payload from the server.
 * Expects a base64 string in the format: IV (16) + AuthTag (16) + CipherText
 */
export async function decryptPayload(
  encryptedPayload: string
): Promise<unknown> {
  const key = await getCryptoKey();
  const combined = fromBase64(encryptedPayload);

  // Server format: IV (16) + AuthTag (16) + CipherText
  const iv = combined.slice(0, 16);
  const authTag = combined.slice(16, 32);
  const cipherText = combined.slice(32);

  // Web Crypto expects: CipherText + AuthTag (tag appended)
  const webCryptoInput = new Uint8Array(cipherText.length + 16);
  webCryptoInput.set(cipherText, 0);
  webCryptoInput.set(authTag, cipherText.length);

  const decrypted = await globalThis.crypto.subtle.decrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    key,
    webCryptoInput
  );

  return JSON.parse(new TextDecoder().decode(decrypted));
}

// ──────────── domain verification helpers ────────────

/**
 * Generate a 16-byte random nonce (hex string).
 * Server caches seen nonces and rejects duplicates within the 30s window.
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  globalThis.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Generate a lightweight browser fingerprint hash.
 * Returns "ssr" in server-side (Node.js) context.
 */
function generateBrowserFingerprint(): string {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "ssr";
  }
  const raw = [
    navigator.userAgent,
    `${screen.width}x${screen.height}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.hardwareConcurrency?.toString() || "unknown",
  ].join("|");

  // DJB2 hash
  let hash = 5381;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 33) ^ raw.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Generate an encrypted domain verification token.
 * Sent as `x-req-trace` header on every main API request.
 * Contains: { d: hostname, t: timestamp, n: nonce, f: fingerprint }
 * Server decrypts, checks domain is allowed, rejects stale/duplicate requests.
 * @param hostname — pass explicitly in SSR context where window is unavailable
 */
export async function generateDomainTrace(hostname?: string): Promise<string> {
  const domain =
    hostname ||
    (typeof window !== "undefined" ? window.location.hostname : "localhost");
  return encryptPayload({
    d: domain,
    t: Date.now(),
    n: generateNonce(),
    f: generateBrowserFingerprint(),
  });
}
